/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { Controller } from "@hotwired/stimulus";
import Chart from "chart.js/auto";

export default class extends Controller {
  static targets = ["firstChartCanvas"];

  static values = {
    estProfitSell: Number,
    downPayment: Number,
    estCostBuy: Number,
    cumulativeMonthlyCostDifference: Number,
  };

  connect() {
    this.firstChart = null;
    this.updateChartData();
  }

  updateChartData() {
    const container = this.firstChartCanvasTarget.parentElement;
    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;

    this.firstChartCanvasTarget.width = containerWidth;
    this.firstChartCanvasTarget.height = containerHeight;
    const isSmScreen = window.innerWidth <= 600;

    const estimatedProfit = Math.round(this.estProfitSellValue);
    const downPayment = Math.round(this.downPaymentValue);
    const estimatedCostsBuy = Math.round(this.estCostBuyValue);
    const cumulativeMonthlyCostDifference = Math.round(
      this.cumulativeMonthlyCostDifferenceValue,
    );

    const profitColor =
      estimatedProfit >= 0
        ? "rgba(75, 192, 192, 0.7)"
        : "rgba(255, 206, 86, 0.7)";
    const profitHoverColor =
      estimatedProfit >= 0 ? "rgba(75, 192, 192, 1)" : "rgba(255, 206, 86, 1)";
    const monthlyCostDiffColor =
      cumulativeMonthlyCostDifference >= 0
        ? "rgba(255, 206, 86, 0.7)"
        : "rgba(75, 192, 192, 0.7)";
    const monthlyCostDiffHoverColor =
      cumulativeMonthlyCostDifference >= 0
        ? "rgba(255, 206, 86, 1)"
        : "rgba(75, 192, 192, 1)";

    const chartData = {
      type: "doughnut",
      data: {
        labels: [
          "Estimated Profit",
          "Down Payment",
          "Estimated Costs Buy",
          "Cumulative Monthly Cost",
        ],
        datasets: [
          {
            data: [
              Math.abs(estimatedProfit),
              downPayment,
              estimatedCostsBuy,
              Math.abs(cumulativeMonthlyCostDifference),
            ],
            backgroundColor: [
              profitColor,
              "rgba(235, 87, 87, 0.7)",
              "rgba(242, 153, 74, 0.7)",
              monthlyCostDiffColor,
            ],
            hoverBackgroundColor: [
              profitHoverColor,
              "rgba(235, 87, 87, 1)",
              "rgba(242, 153, 74, 1)",
              monthlyCostDiffHoverColor,
            ],
          },
        ],
      },
      options: {
        maintainAspectRatio: false,
        responsive: true,
        cutout: "60%",
        layout: {
          padding: isSmScreen
            ? {
                top: containerHeight * 0.27,
                right: containerWidth * 0.27,
                bottom: containerHeight * 0.27,
                left: containerWidth * 0.27,
              }
            : {
                top: containerHeight * 0.2,
                right: containerWidth * 0.2,
                bottom: containerHeight * 0.2,
                left: containerWidth * 0.2,
              },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: function (context) {
                const label = context.label || "";
                const value = context.raw.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                  maximumFractionDigits: 0,
                });
                return `${label}: ${value}`;
              },
            },
          },
          externalLabels: { enabled: true },
        },
      },
    };

    this.createOrUpdateChart(
      this.firstChartCanvasTarget,
      chartData,
      "firstChart",
    );
  }

  createOrUpdateChart(canvasTarget, chartData, chartInstanceName) {
    const ctx = canvasTarget.getContext("2d");

    if (!Chart.registry.plugins.get("externalLabels")) {
      Chart.registry.plugins.register({
        id: "externalLabels",
        afterDraw: (chart) => {
          if (!chart.options.plugins.externalLabels?.enabled) return;

          const { ctx } = chart;
          ctx.save();

          const meta = chart.getDatasetMeta(0);
          const centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
          const centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;
          const radius =
            Math.min(
              chart.chartArea.right - chart.chartArea.left,
              chart.chartArea.bottom - chart.chartArea.top,
            ) / 2;

          const isSmScreen = window.innerWidth <= 600;
          const fontSize = isSmScreen ? 11 : 11;
          const valueFontSize = isSmScreen ? 11 : 12;
          const labelRects = [];
          meta.data.forEach((arc, i) => {
            if (arc.hidden) return;

            const angle = arc.startAngle + (arc.endAngle - arc.startAngle) / 2;
            const label = chart.data.labels[i];
            const value = chart.data.datasets[0].data[i].toLocaleString(
              "en-US",
              { style: "currency", currency: "USD", maximumFractionDigits: 0 },
            );
            const outerRadius = radius * 1;
            const innerX = centerX + Math.cos(angle) * outerRadius;
            const innerY = centerY + Math.sin(angle) * outerRadius;

            let radiusOffset = 0;
            let angleOffset = 0;

            if (label === "Estimated Costs Buy") {
              radiusOffset = isSmScreen ? radius * 0.08 : radius * 0.1;
              angleOffset = -Math.PI * 0.03;
            } else if (label === "Cumulative Monthly Cost") {
              radiusOffset = isSmScreen ? radius * 0.15 : radius * 0.2;
              angleOffset = Math.PI * 0.03;
            }

            const adjustedAngle = angle + angleOffset;
            const labelRadius = radius * 1.4 + radiusOffset;
            const labelX = centerX + Math.cos(adjustedAngle) * labelRadius;
            const labelY = centerY + Math.sin(adjustedAngle) * labelRadius;

            const textAlign =
              adjustedAngle > Math.PI / 2 && adjustedAngle < (3 * Math.PI) / 2
                ? "right"
                : "left";
            const textOffset = textAlign === "right" ? -4 : 4;

            const segmentColor = chart.data.datasets[0].backgroundColor[
              i
            ].replace("0.7", "1");
            ctx.beginPath();
            ctx.moveTo(innerX, innerY);
            ctx.lineTo(labelX, labelY);
            ctx.strokeStyle = segmentColor;
            ctx.lineWidth = 1.2;
            ctx.stroke();

            let lines = [];
            if (label === "Down Payment") {
              lines = ["Down", "Payment"];
            } else {
              const words = label.split(" ");
              let currentLine = words[0];

              for (let j = 1; j < words.length; j++) {
                if (currentLine.length + words[j].length + 1 <= 12) {
                  currentLine += " " + words[j];
                } else {
                  lines.push(currentLine);
                  currentLine = words[j];
                }
              }
              lines.push(currentLine);
            }

            ctx.font = `${fontSize}px Montserrat, sans-serif`;
            ctx.textAlign = textAlign;
            ctx.textBaseline = "middle";
            ctx.fillStyle = "#333";

            const lineHeight = fontSize + 4;
            const totalTextHeight = lines.length * lineHeight;

            lines.forEach((line, lineIndex) => {
              const lineY =
                labelY +
                (lineIndex * lineHeight - (totalTextHeight - lineHeight) / 2);
              ctx.fillText(line, labelX + textOffset, lineY);
            });

            ctx.font = `bold ${valueFontSize}px Montserrat, sans-serif`;
            ctx.fillText(
              value,
              labelX + textOffset,
              labelY + totalTextHeight / 2 + 12,
            );

            const textWidth = Math.max(
              ...lines.map((line) => ctx.measureText(line).width),
              ctx.measureText(value).width,
            );
            const textHeight = totalTextHeight + valueFontSize + 14;

            labelRects.push({
              x:
                textAlign === "left"
                  ? labelX + textOffset
                  : labelX - textWidth - Math.abs(textOffset),
              y: labelY - textHeight / 2,
              width: textWidth + Math.abs(textOffset) * 2,
              height: textHeight,
              label,
            });
          });

          ctx.restore();
        },
      });
    }

    if (this[chartInstanceName]) {
      this[chartInstanceName].data = chartData.data;
      this[chartInstanceName].options = chartData.options;
      this[chartInstanceName].update();
    } else {
      if (Chart.getChart(ctx)) {
        Chart.getChart(ctx).destroy();
      }
      this[chartInstanceName] = new Chart(ctx, chartData);
    }
  }

  disconnect() {
    if (this.firstChart) {
      this.firstChart.destroy();
      this.firstChart = null;
    }
  }
}
